import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Table, Button, Spinner, InputGroup, FormControl, Alert } from 'react-bootstrap';
import { FaRobot, FaCalendarAlt, FaDollarSign, FaHeadset, FaCopy, FaDownload, FaSyncAlt, FaExclamationTriangle, FaHistory } from 'react-icons/fa';
import api from '../services/api';
import moment from 'moment';

const BotManagement = ({ user: initialUser }) => {
    const [data, setData] = useState({ active_bots: [], user: initialUser });
    const [loading, setLoading] = useState(true);
    const [copySuccess, setCopySuccess] = useState('');

    useEffect(() => {
        fetchMyBots();
    }, []);

const fetchMyBots = async () => {
    try {
        setLoading(true);
        // Sửa đường dẫn cho đúng cấu hình /api/
        const res = await api.get('/api/order/my-orders'); 
        
        if (res.data.success && res.data.orders) {
            console.log("📊 [DEBUG] Danh sách đơn hàng từ server:", res.data.orders);

            // Bỏ bớt điều kiện lọc quá khắt khe để xem dữ liệu có về không
            const activeOrders = res.data.orders.filter(order => order.accountId);

            const uniqueBots = {};
            activeOrders.forEach(order => {
                // Ưu tiên dùng magic_number, nếu không có thì dùng packageId để định danh
                const magic = order.magic_number || order.packageId?._id;
                
                // Nếu chưa có bot này hoặc đơn hàng này mới hơn thì ghi đè
                if (!uniqueBots[magic] || moment(order.createdAt).isAfter(moment(uniqueBots[magic].createdAt))) {
                    uniqueBots[magic] = order;
                }
            });

            const finalBots = Object.values(uniqueBots).map(order => ({
                bot_id: order.packageId || { name: "NeXBot Trader" },
                magic: order.magic_number || "N/A",
                mt5_account: order.accountId,
                api_key: order.apiKey || 'ĐANG ĐỒNG BỘ...', // Hiển thị thông báo nếu chưa có key
                bot_type: order.packageId?.name || "NeXBot Trader",
                updated_at: order.updatedAt || new Date()
            }));

            setData({ active_bots: finalBots, user: initialUser });
        }
    } catch (err) {
        console.error("❌ Lỗi API BotManagement:", err);
    } finally {
        setLoading(false);
    }
};

    const handleCopyKey = (key) => {
        navigator.clipboard.writeText(key);
        setCopySuccess(key);
        setTimeout(() => setCopySuccess(''), 2000);
    };

    const handleDownload = (bot) => {
        const packageCode = bot.bot_id?.package_code;
        if (!packageCode) {
            return alert("Tập tin mã nguồn đang được cập nhật, vui lòng quay lại sau ít phút.");
        }
        const downloadUrl = `https://nexbot.vn/api/packages/download/${packageCode}`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        setTimeout(() => { document.body.removeChild(link); }, 100);
    };

    if (loading) return (
        <div className="text-center py-5">
            <Spinner animation="border" variant="warning" />
            <p className="mt-2 text-muted fw-bold">ĐANG KẾT NỐI HỆ THỐNG TRUNG TÂM...</p>
        </div>
    );

    return (
        <Container fluid className="py-4 bg-light min-vh-100">
            {/* --- HEADER --- */}
            <div className="page-banner mb-4 shadow-lg" style={{ 
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', 
                padding: '30px', 
                borderRadius: '15px', 
                color: 'white', 
                borderLeft: '10px solid #eab308' 
            }}>
                <h1 className="fw-bold mb-1"><FaRobot className="me-2 text-warning"/> TRUNG TÂM QUẢN LÝ ROBOT</h1>
                <p className="opacity-75 mb-0 small text-uppercase">Tải phiên bản cập nhật mới nhất và dán API Key để vận hành</p>
            </div>

            <Row className="g-4">
                <Col lg={4}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
                        <Card.Header className="bg-white border-0 pt-4 px-4 text-center">
                            <h5 className="fw-bold text-dark mb-0">HỒ SƠ VẬN HÀNH</h5>
                        </Card.Header>
                        <Card.Body className="px-4">
                            <div className="mb-4 text-center">
                                <Badge bg={data.active_bots.length > 0 ? "success" : "secondary"} className="px-4 py-2 fs-6 w-100 shadow-sm rounded-pill">
                                    {data.active_bots.length > 0 ? "🟢 ĐANG SỞ HỮU " + data.active_bots.length + " LOẠI BOT" : "⚪ CHƯA CÓ BOT"}
                                </Badge>
                            </div>
                            
                            <div className="d-flex align-items-center mb-3 p-3 bg-light rounded-3 border-start border-primary border-4">
                                <FaCalendarAlt className="text-primary me-3 fs-4" />
                                <div>
                                    <small className="text-muted d-block fw-bold">NGÀY THAM GIA</small>
                                    <span className="fw-bold text-dark">{moment(data.user?.createdAt).format('DD/MM/YYYY')}</span>
                                </div>
                            </div>

                            <div className="d-flex align-items-center mb-3 p-3 bg-light rounded-3 border-start border-success border-4">
                                <FaDollarSign className="text-success me-3 fs-4" />
                                <div>
                                    <small className="text-muted d-block fw-bold">EMAIL ĐĂNG KÝ</small>
                                    <span className="fw-bold text-dark small">{data.user?.email || 'N/A'}</span>
                                </div>
                            </div>

                            <Alert variant="warning" className="mt-4 border-0 shadow-sm py-2">
                                <h6 className="fw-bold small"><FaExclamationTriangle className="me-2"/> THÔNG BÁO CẬP NHẬT</h6>
                                <p className="small mb-0" style={{fontSize: '11px'}}>Khi nhận được email thông báo, vui lòng nhấn "TẢI .EX5" để nhận phiên bản Robot mới nhất.</p>
                            </Alert>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={8}>
                    <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
                        <Card.Header className="bg-white border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                            <div>
                                <h5 className="fw-bold text-dark mb-0">PHIÊN BẢN ROBOT MỚI NHẤT</h5>
                                <small className="text-primary fw-bold text-uppercase" style={{fontSize: '11px'}}>Hệ thống tự động lọc phiên bản mới nhất theo Magic Number</small>
                            </div>
                            <Button variant="outline-dark" size="sm" onClick={fetchMyBots} className="rounded-pill px-3 fw-bold">
                                <FaSyncAlt className="me-1" /> LÀM MỚI
                            </Button>
                        </Card.Header>
                        <Card.Body className="px-4 pb-4">
                            {data.active_bots.length === 0 ? (
                                <div className="text-center py-5">
                                    <FaExclamationTriangle size={50} className="text-warning mb-3 opacity-30" />
                                    <h6 className="text-muted fw-bold text-uppercase">Chưa có dữ liệu Robot</h6>
                                </div>
                            ) : (
                                <Table hover responsive borderless className="align-middle">
                                    <thead className="bg-light text-muted small">
                                        <tr>
                                            <th className="py-3">ROBOT & MAGIC</th>
                                            <th>API KEY (DÁN VÀO BOT)</th>
                                            <th>PHIÊN BẢN CẬP NHẬT</th>
                                            <th className="text-center">HÀNH ĐỘNG</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.active_bots.map((bot, index) => (
                                            <tr key={index} className="border-bottom">
                                                <td className="py-3">
                                                    <div className="fw-bold text-dark">
                                                        {bot.bot_id?.name || "NeXBot"}
                                                    </div>
                                                    <div className="d-flex gap-1 mt-1">
                                                        <Badge bg="primary" style={{fontSize: '9px'}}>MT5: {bot.mt5_account}</Badge>
                                                        <Badge bg="secondary" style={{fontSize: '9px'}}>MAGIC: {bot.magic}</Badge>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center bg-light p-1 rounded border">
                                                        <code className="small me-2 text-primary fw-bold" style={{fontSize: '10px'}}>{bot.api_key}</code>
                                                        <Button 
                                                            variant="link" 
                                                            className="p-1 shadow-none rounded-circle"
                                                            onClick={() => handleCopyKey(bot.api_key)}
                                                            style={{ 
                                                                backgroundColor: copySuccess === bot.api_key ? '#198754' : '#d4a017',
                                                                color: 'white',
                                                                width: '24px',
                                                                height: '24px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            {copySuccess === bot.api_key ? <span style={{fontSize: '10px'}}>✓</span> : <FaCopy size={12}/>}
                                                        </Button>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="small text-dark fw-bold">
                                                        <FaHistory className="me-1 text-muted" size={10}/>
                                                        Ngày: {bot.updated_at ? moment(bot.updated_at).format('DD/MM/YYYY') : '01/01/2026'}
                                                    </div>
                                                    <div className="text-muted" style={{fontSize: '10px'}}>Trạng thái: Đã tối ưu</div>
                                                </td>
                                                <td className="text-center">
                                                    <Button 
                                                        variant="warning" 
                                                        size="sm" 
                                                        className="fw-bold px-3 shadow-sm rounded-pill btn-hover-scale"
                                                        onClick={() => handleDownload(bot)}
                                                    >
                                                        <FaDownload className="me-1"/> TẢI .EX5
                                                    </Button>
                                                    <div className="text-muted mt-1" style={{fontSize: '8px'}}>Build: Latest Version</div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <style>{`
                .btn-hover-scale:hover { transform: scale(1.05); transition: 0.2s; }
            `}</style>
        </Container>
    );
};

export default BotManagement;